import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fieldWorkApi } from '../api/field-work-api';
import { AppliedFilters } from '../components/AppliedFilters';
import { ErrorState } from '../components/ErrorState';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import { SortableTableHeader } from '../components/SortableTableHeader';
import { useAuth } from '../auth/AuthContext';
import {
  compareQueueOrder,
  currentHandoff,
  fieldWorkSources,
  fieldWorkStatuses,
  isOpenStatus,
  priorityLabels,
  sourceLabels,
  statusLabels,
  type FieldWorkRequest,
} from '../domain/fieldWorkRequest';
import { fieldOpsTeams, teamLabels, type FieldOpsTeam } from '../domain/fieldOpsTeam';
import { NewFieldWorkRequestDrawer } from '../features/field-work/NewFieldWorkRequestDrawer';
import { PickListPrompt } from '../features/field-work/PickListPrompt';
import {
  matchesFieldWorkSearch,
  useFieldWorkPlaceNames,
} from '../features/field-work/field-work-search';

const PAGE_SIZE = 25;
const sortKeys = [
  'title',
  'customer',
  'source',
  'requester',
  'priority',
  'assignee',
  'status',
  'due',
] as const;
type SortKey = (typeof sortKeys)[number];

function isSortKey(value: string | null): value is SortKey {
  return sortKeys.some((key) => key === value);
}

function isTeam(value: string | null): value is FieldOpsTeam {
  return fieldOpsTeams.some((team) => team === value);
}

function compare(a: FieldWorkRequest, b: FieldWorkRequest, key: SortKey): number {
  switch (key) {
    case 'title':
      return a.title.localeCompare(b.title);
    case 'customer':
      return (a.customerId ?? '').localeCompare(b.customerId ?? '');
    case 'source':
      return (a.source ?? '').localeCompare(b.source ?? '');
    case 'requester':
      return (a.requestedBy ?? '').localeCompare(b.requestedBy ?? '');
    case 'priority':
      return compareQueueOrder(a, b);
    case 'assignee':
      return a.assignee.localeCompare(b.assignee);
    case 'status':
      return a.status.localeCompare(b.status);
    case 'due':
      return (a.dueDate ?? '￿').localeCompare(b.dueDate ?? '￿');
  }
}

export function FieldWorkPage() {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  // Just created: ask whether it needs a pick list (Field Services, #8201).
  const [justCreated, setJustCreated] = useState<FieldWorkRequest | null>(null);
  const [params, setParams] = useSearchParams();

  const teamParam = params.get('team');
  const teamFilter = isTeam(teamParam) ? teamParam : null;
  const statusFilter = params.get('status') ?? '';
  const sourceFilter = params.get('source') ?? '';
  // `assignee` is an exact email or the literal 'unassigned'; `requester`
  // is an exact requestedBy email. Both are set by clicking a cell or a
  // reports row, and shown as removable chips.
  const assigneeFilter = params.get('assignee') ?? '';
  const requesterFilter = params.get('requester') ?? '';
  const mineOnly = params.get('mine') === 'true';
  // Completed and rejected requests are hidden unless asked for, or unless
  // the status filter itself picks a closed status.
  const closedRequested = params.get('closed') === 'true';
  const includeClosed =
    closedRequested ||
    (statusFilter !== '' && !isOpenStatus(statusFilter as FieldWorkRequest['status']));
  const query = params.get('q') ?? '';
  const sortParam = params.get('sort');
  const sortKey = isSortKey(sortParam) ? sortParam : null;
  const sortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const requestedPage = Math.max(1, Number(params.get('page') ?? 1) || 1);

  const requests = useQuery({
    queryKey: ['field-work'],
    queryFn: () => fieldWorkApi.listFieldWork(),
  });
  const data = useMemo(() => requests.data ?? [], [requests.data]);
  const placeNames = useFieldWorkPlaceNames();
  const closedHidden = includeClosed
    ? 0
    : data.filter((request) => !isOpenStatus(request.status)).length;

  const filtered = useMemo(() => {
    const matching = data.filter((request) => {
      if (!includeClosed && !isOpenStatus(request.status)) return false;
      if (teamFilter && request.team !== teamFilter) return false;
      if (statusFilter && request.status !== statusFilter) return false;
      if (sourceFilter && (request.source ?? '') !== sourceFilter) return false;
      if (mineOnly && request.assignee !== (user?.email ?? '')) return false;
      if (assigneeFilter === 'unassigned' && request.assignee !== '') return false;
      if (assigneeFilter && assigneeFilter !== 'unassigned' && request.assignee !== assigneeFilter) {
        return false;
      }
      if (requesterFilter && (request.requestedBy ?? '') !== requesterFilter) return false;
      return matchesFieldWorkSearch(request, query, placeNames);
    });
    return matching.sort((a, b) => {
      if (!sortKey) return compareQueueOrder(a, b);
      const result = compare(a, b, sortKey);
      return sortDirection === 'asc' ? result : -result;
    });
  }, [
    data,
    includeClosed,
    placeNames,
    teamFilter,
    statusFilter,
    sourceFilter,
    assigneeFilter,
    requesterFilter,
    mineOnly,
    user?.email,
    query,
    sortKey,
    sortDirection,
  ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    if (name !== 'page') next.delete('page');
    setParams(next);
  }

  function updateSort(key: string): void {
    if (!isSortKey(key)) return;
    const next = new URLSearchParams(params);
    if (sortKey === key) {
      if (sortDirection === 'asc') next.set('dir', 'desc');
      else {
        next.delete('sort');
        next.delete('dir');
      }
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    next.delete('page');
    setParams(next);
  }

  if (requests.isLoading) return <PageLoadingState label="Loading field work…" />;
  if (requests.isError) {
    return (
      <ErrorState
        title="Field work could not be loaded"
        rpcName="ListFieldWorkRequests"
        onRetry={() => void requests.refetch()}
      />
    );
  }

  const activeFilters = [
    query.trim()
      ? { key: 'q', label: `Search: ${query.trim()}`, onRemove: () => setParam('q', '') }
      : null,
    statusFilter
      ? {
          key: 'status',
          label: `Status: ${statusLabels[statusFilter as FieldWorkRequest['status']]}`,
          onRemove: () => setParam('status', ''),
        }
      : null,
    sourceFilter
      ? {
          key: 'source',
          label: `Source: ${sourceLabels[sourceFilter as FieldWorkRequest['source'] & string] ?? sourceFilter}`,
          onRemove: () => setParam('source', ''),
        }
      : null,
    assigneeFilter
      ? {
          key: 'assignee',
          label:
            assigneeFilter === 'unassigned'
              ? 'Assignee: Unassigned'
              : `Assignee: ${assigneeFilter}`,
          onRemove: () => setParam('assignee', ''),
        }
      : null,
    requesterFilter
      ? {
          key: 'requester',
          label: `Requested by: ${requesterFilter}`,
          onRemove: () => setParam('requester', ''),
        }
      : null,
    mineOnly
      ? { key: 'mine', label: 'My work', onRemove: () => setParam('mine', '') }
      : null,
    closedRequested
      ? { key: 'closed', label: 'Including closed', onRemove: () => setParam('closed', '') }
      : null,
    sortKey
      ? {
          key: 'sort',
          label: `Sort: ${sortKey} (${sortDirection})`,
          onRemove: () => {
            const next = new URLSearchParams(params);
            next.delete('sort');
            next.delete('dir');
            setParams(next);
          },
        }
      : null,
  ].filter((f): f is { key: string; label: string; onRemove: () => void } => f !== null);

  return (
    <>
      <PageHeader
        title="Field work"
        description="The Field Ops queue — triage, prioritize, assign and track day-to-day work."
        actions={
          <>
            <Link to="/field-work/board" className="button-link">
              Board
            </Link>
            <Link to="/field-work/reports" className="button-link">
              Reports
            </Link>
            <Link to="/field-work/settings" className="button-link">
              Queue owners
            </Link>
            <button type="button" className="primary" onClick={() => setCreating(true)}>
              New request
            </button>
          </>
        }
      />

      {creating ? (
        <NewFieldWorkRequestDrawer
          defaultTeam={teamFilter ?? undefined}
          onClose={() => setCreating(false)}
          onCreated={(request) => {
            setCreating(false);
            setJustCreated(request);
          }}
        />
      ) : null}
      {justCreated ? (
        <PickListPrompt request={justCreated} onDone={() => setJustCreated(null)} />
      ) : null}

      <div className="region-tabs" role="tablist" aria-label="Field Ops team">
        <button
          type="button"
          role="tab"
          aria-selected={teamFilter === null}
          className={`region-tab${teamFilter === null ? ' is-active' : ''}`}
          onClick={() => setParam('team', '')}
        >
          All teams
        </button>
        {fieldOpsTeams.map((team) => (
          <button
            key={team}
            type="button"
            role="tab"
            aria-selected={teamFilter === team}
            className={`region-tab${teamFilter === team ? ' is-active' : ''}`}
            onClick={() => setParam('team', team)}
          >
            {teamLabels[team]}
          </button>
        ))}
      </div>

      <section className="reference-workspace" aria-label="Field work requests">
        <form
          className="requests-controls"
          role="search"
          aria-label="Filter field work"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="search-field">
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setParam('q', event.target.value)}
              placeholder="Title, site, customer or person"
              aria-label="Search field work"
            />
          </label>
          <label>
            <span>Status</span>
            <SearchableSelect
              ariaLabel="Status"
              value={statusFilter}
              onChange={(value) => setParam('status', value)}
              options={[
                { value: '', label: 'All statuses' },
                ...fieldWorkStatuses.map((status) => ({
                  value: status,
                  label: statusLabels[status],
                })),
              ]}
              placeholder="All statuses"
            />
          </label>
          <label>
            <span>Source</span>
            <SearchableSelect
              ariaLabel="Source"
              value={sourceFilter}
              onChange={(value) => setParam('source', value)}
              options={[
                { value: '', label: 'All sources' },
                ...fieldWorkSources.map((source) => ({
                  value: source,
                  label: sourceLabels[source],
                })),
              ]}
              placeholder="All sources"
            />
          </label>
          <label className="checkbox-field">
            <span>My work</span>
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(event) => setParam('mine', event.target.checked ? 'true' : '')}
              aria-label="Show only work assigned to me"
            />
          </label>
          <label className="checkbox-field">
            <span>Include closed</span>
            <input
              type="checkbox"
              checked={closedRequested}
              onChange={(event) => setParam('closed', event.target.checked ? 'true' : '')}
              aria-label="Include completed and rejected requests"
            />
          </label>
        </form>
        <AppliedFilters filters={activeFilters} />
        {closedHidden > 0 ? (
          <p className="field-hint">
            {closedHidden} completed or rejected{' '}
            {closedHidden === 1 ? 'request is' : 'requests are'} hidden.{' '}
            <button
              type="button"
              className="table-filter-link"
              onClick={() => setParam('closed', 'true')}
            >
              Show them
            </button>
          </p>
        ) : null}

        {filtered.length === 0 ? (
          <div className="empty-state">
            <strong>No field work matches these filters.</strong>
            <button type="button" onClick={() => setParams(new URLSearchParams())}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="table-wrap reference-table">
            <table>
              <thead>
                <tr>
                  <SortableTableHeader label="Title" sortKey="title" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Team" sortKey="customer" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Source" sortKey="source" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Requested by" sortKey="requester" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Priority" sortKey="priority" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Assignee" sortKey="assignee" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Status" sortKey="status" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                  <SortableTableHeader label="Due" sortKey="due" activeKey={sortKey} direction={sortDirection} onSort={updateSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((request) => (
                  <tr key={request.id}>
                    <th scope="row">
                      <Link to={`/field-work/${request.id}`}>{request.title || 'Untitled request'}</Link>
                      {(() => {
                        const handoff = currentHandoff(request);
                        return handoff ? (
                          <p className="handoff-hint" title={handoff.note}>
                            Handed to {handoff.toAssignee === user?.email ? 'you' : handoff.toAssignee}{' '}
                            by {handoff.author ?? 'someone'}: {handoff.note}
                          </p>
                        ) : null;
                      })()}
                    </th>
                    <td>
                      <button
                        type="button"
                        className="table-filter-link"
                        onClick={() => setParam('team', request.team)}
                      >
                        {teamLabels[request.team]}
                      </button>
                    </td>
                    <td>
                      {request.source ? (
                        <button
                          type="button"
                          className="table-filter-link"
                          onClick={() => setParam('source', request.source ?? '')}
                        >
                          {sourceLabels[request.source]}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {request.requestedBy ? (
                        <button
                          type="button"
                          className="table-filter-link"
                          onClick={() => setParam('requester', request.requestedBy ?? '')}
                        >
                          {request.requestedBy}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{request.priority ? priorityLabels[request.priority] : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="table-filter-link"
                        onClick={() =>
                          setParam('assignee', request.assignee || 'unassigned')
                        }
                      >
                        {request.assignee || 'Unassigned'}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="state-badge-button"
                        onClick={() => setParam('status', request.status)}
                        title={`Filter to ${statusLabels[request.status]}`}
                      >
                        <span
                          className={
                            request.status === 'BLOCKED'
                              ? 'state-badge state-badge-attention'
                              : request.status === 'COMPLETE'
                                ? 'state-badge'
                                : 'state-badge state-badge-ready'
                          }
                        >
                          {statusLabels[request.status]}
                        </span>
                      </button>
                    </td>
                    <td className="mono">
                      {request.dueDate ? new Date(request.dueDate).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 ? (
          <PageControls
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            onPageChange={(nextPage) => setParam('page', nextPage <= 1 ? '' : String(nextPage))}
            itemLabel="requests"
          />
        ) : null}
      </section>
    </>
  );
}
